import { Link } from "react-router-dom";
import { ArrowUpRight, FileDown } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines, SectionHead } from "@/components/Reveal";

const GROUPS_EMG = [
  {
    group: "Positioning",
    rows: [
      ["System accuracy", "±30 mm (manufacturer rating)"],
      ["GNSS", "Dual antennas · GPS, GLONASS, BeiDou, Galileo, QZSS · L-Band"],
      ["Antenna rating", "IP68"],
    ],
  },
  {
    group: "Sensors",
    rows: [
      ["IMU accuracy", "±0.05° static / ±0.1° dynamic"],
      ["IMU rating", "IP69K · 115 g · 48.9 × 109 × 27.3 mm"],
      ["Power input", "7–36 V DC"],
      ["Operating temp", "−40°C to +75°C"],
    ],
  },
  {
    group: "Display",
    rows: [
      ["Screen", '10.1" · 1024 × 600 · 750 cd/m²'],
      ["Compute", "Android · 4-core 1.2 GHz · 2 GB / 16 GB"],
      ["Rating", "IP65 · 281 × 181 × 42 mm · 1.5 kg"],
      ["Operating temp", "−30°C to +70°C"],
    ],
  },
  {
    group: "Connectivity",
    rows: [
      ["Wireless", "Wi-Fi · Bluetooth · 4G LTE"],
      ["Radio", "External radio support"],
      ["Ports", "CAN · RS232"],
    ],
  },
  {
    group: "Machine fit",
    rows: [
      ["Boom types", "Swing boom · triple boom · tilt bucket · offset boom"],
      ["Install & calibration", "<2 h mounting · ~30 min calibration per bucket"],
      ["Guidance", "Depth & slope from field reference · user-defined buckets · live cut/fill volumes"],
    ],
  },
];

const GROUPS_TX = [
  {
    group: "Positioning",
    rows: [
      ["System accuracy", "10 mm horizontal / 15 mm vertical (manufacturer rating)"],
      ["MC300 GNSS receiver", "RTK 8 mm + 1 ppm H · 15 mm + 1 ppm V"],
      ["Receiver rating", "IP67"],
      ["Antennas", "Dual AT315 · IP68"],
    ],
  },
  {
    group: "Sensors",
    rows: [
      ["IS300 IMU", "100 Hz · ±0.05° static / ±0.1° dynamic"],
      ["IMU rating", "IP69K"],
    ],
  },
  {
    group: "Display",
    rows: [
      ["MCPad300", '10.1" · 1920 × 1200 · IP67'],
      ["Compute", "Android 14 · 8-core 2.2 GHz · 6 GB / 64 GB"],
      ["Views", "2D & 3D · color cut/fill maps · customizable operator shortcuts"],
    ],
  },
  {
    group: "Connectivity",
    rows: [
      ["Wireless", "4G LTE · Wi-Fi · Bluetooth"],
      ["Radio", "410–470 MHz modem"],
      ["Ports", "CAN · RS232"],
    ],
  },
  {
    group: "Design data",
    rows: [
      ["Formats", "LandXML · DWG · DXF · .dc · .cal · .jxl · .lok · .loc"],
      ["Cloud", "iSite — OTA updates · remote troubleshooting · data sync"],
    ],
  },
  {
    group: "Attachments",
    rows: [
      ["Supported", "Standard buckets · tilt buckets · Engcon & Steelwrist tilt-rotators"],
      ["Tilt control", "Automated on supported tilt-rotators"],
    ],
  },
];

const GROUPS_TD63 = [
  {
    group: "Positioning",
    rows: [
      ["Blade accuracy", "±20 mm — dual GNSS + 100 Hz IMU + DO-Tech (manufacturer rating)"],
      ["Max precision", "10 mm H / 15 mm V"],
      ["GNSS", "Full GNSS · dual antennas"],
    ],
  },
  {
    group: "Sensors",
    rows: [
      ["IMU", "100 Hz tilt sensor"],
      ["Valve module", "Pilot & PVG dozer valve integration"],
    ],
  },
  {
    group: "Display",
    rows: [
      ["Screen", '10.1" tablet'],
      ["Calibration", "~30 min · single IMU sensor"],
    ],
  },
  {
    group: "Connectivity",
    rows: [
      ["Wireless", "Wi-Fi · Bluetooth"],
      ["Cellular", "4G LTE"],
      ["Radio", "410–470 MHz Rx"],
      ["Ports", "CAN · RS232"],
    ],
  },
  {
    group: "Applications",
    rows: [
      ["Use cases", "Land clearing · foundation preparation · dam & levee · embankment construction"],
    ],
  },
];

const GROUPS_I83 = [
  {
    group: "Positioning",
    rows: [
      ["RTK", "8 mm + 1 ppm H / 15 mm + 1 ppm V (manufacturer rating)"],
      ["RTK init", "<10 s · >99.9% reliability"],
      ["PPP", "B2b-PPP / E6B-HAS · 100 mm H / 200 mm V"],
      ["GNSS", "1408 channels · iStar2.0 · GPS, GLONASS, Galileo, BeiDou, QZSS, NavIC, SBAS"],
    ],
  },
  {
    group: "Sensors",
    rows: [
      ["IMU", "200 Hz AUTO-IMU · calibration-free"],
      ["Tilt compensation", "0–60° · E-Bubble levelling · magnetically immune"],
    ],
  },
  {
    group: "Connectivity",
    rows: [
      ["Wireless", "Wi-Fi 802.11 a/b/g/n/ac · Bluetooth 5.0 · NFC"],
      ["Cellular", "4G LTE"],
      ["Radio", "UHF 410–470 MHz Rx/Tx · 3–5 km typical, up to 15 km"],
      ["Protocols", "RTCM 2.x/3.x · CMR · NMEA 0183 · RINEX · NTRIP"],
    ],
  },
  {
    group: "Physical",
    rows: [
      ["Weight", "1.15 kg"],
      ["Dimensions", "Ø152 × 78 mm"],
      ["Rating", "IP67 · MIL-STD-810H · 2 m pole drop"],
      ["Battery", "9,900 mAh — up to 34 h RTK rover · 16 h RTK base"],
      ["Storage", "8 GB internal"],
      ["Operating temp", "−40°C to +65°C"],
    ],
  },
];

function SpecTable({ id, testid, badge, name, groups }) {
  return (
    <div id={id} data-testid={testid} className="scroll-mt-28 border border-line bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line bg-deep px-5 py-4 sm:px-8">
        <div>
          <span className="border border-signal/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
            {badge}
          </span>
          <h3 className="mt-2 font-display text-2xl font-black uppercase text-white sm:text-3xl">{name}</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/spec-sheets/${id}`}
            download
            data-testid={`spec-pdf-${id}`}
            className="inline-flex items-center gap-2 border border-signal/50 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon transition-colors hover:bg-signal/10"
          >
            <FileDown className="h-4 w-4" />
            Spec sheet PDF
          </a>
          <Link
            to="/#quote"
            data-testid={`${testid}-cta`}
            className="group inline-flex items-center gap-2 bg-brand px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
          >
            Request a quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
      <div className="px-5 sm:px-8">
        {groups.map((g) => (
          <div key={g.group} className="py-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">{g.group}</p>
            <div className="mt-2">
              {g.rows.map(([k, v]) => (
                <div
                  key={k}
                  className="flex flex-col gap-1 border-b border-line/60 py-3 sm:flex-row sm:items-baseline sm:justify-between"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{k}</span>
                  <span className="text-sm text-white sm:max-w-[65%] sm:text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ANCHORS = [
  ["emg100", "EasyNAV EMG100 · 2.5D"],
  ["tx73", "MCNAV TX73 · 3D"],
  ["td63", "TD63 Pro · Dozer"],
  ["i83", "i83 · Rover"],
];

export default function Specifications() {
  return (
    <main>
      <section className="relative overflow-hidden border-b border-line px-4 pb-14 pt-32 sm:px-8 lg:px-16 lg:pb-20 lg:pt-40" data-testid="specifications-hero">
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto max-w-[1600px]">
          <FadeUp y={12}>
            <Eyebrow>Technical library</Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.12}
            lines={["Every number.", { text: "Every system.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Full manufacturer specifications for the whole{" "}
            <Link to="/precision-solutions" data-testid="specs-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
              CHCNAV
            </Link>{" "}
            lineup, straight from the published datasheets.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {ANCHORS.map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`spec-tab-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border border-signal/50 bg-ink/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon backdrop-blur-sm transition-colors hover:bg-signal/10"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-8 lg:px-16 lg:py-24" data-testid="specifications-body">
        <div className="mx-auto max-w-[1200px] space-y-16">
          <FadeUp>
            <SpecTable id="emg100" testid="spec-table-emg100" badge="2.5D System" name="EasyNAV EMG100" groups={GROUPS_EMG} />
          </FadeUp>
          <FadeUp>
            <SpecTable id="tx73" testid="spec-table-tx73" badge="3D System" name="MCNAV TX73" groups={GROUPS_TX} />
          </FadeUp>
          <FadeUp>
            <SpecTable id="td63" testid="spec-table-td63" badge="Dozer System" name="TD63 Pro" groups={GROUPS_TD63} />
          </FadeUp>
          <FadeUp>
            <SpecTable id="i83" testid="spec-table-i83" badge="Survey Rover" name="i83" groups={GROUPS_I83} />
          </FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog/60">
            Figures are manufacturer ratings from published{" "}
            <Link to="/precision-solutions" data-testid="specs-note-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
              CHCNAV
            </Link>{" "}
            datasheets. Field performance depends on site
            conditions, RTK corrections and installation quality.
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-line" data-testid="specifications-cta">
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto max-w-[1600px] px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
          <SectionHead
            kicker="Spec sheet questions?"
            title="Talk to a specialist"
            body="Tell us what you're building and we'll match the right system — no spec-sheet archaeology required."
          />
          <FadeUp delay={0.15}>
            <Link
              to="/contact"
              data-testid="specifications-cta-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
